import { useState, useCallback, useMemo, memo } from 'react'
import CalendarMonthView from 'components/CalendarView/CalendarMonthView'
import styles from './PostsCalendarView.pcss'
import SimpleLoader from 'components/SimpleLoader'
import Backdrop from '@mui/material/Backdrop'
import { CalendarDay } from 'interfaces/Post/Calendar'
import { PlannedPost } from 'interfaces'
import CalendarWeekView from 'components/CalendarView/components/CalendarWeekView'
import { useDispatch, useSelector } from 'react-redux'
import {
  calendarMonthStartSelector,
  calendarPostSizeSelector,
  calendarWeekStartSelector,
  postsWeekViewExpandedSelector,
  selectedProfilesSelector
} from 'services/post/selectors'
import { setWeekViewExpanded } from 'services/post'
import { DndContext, MeasuringStrategy, useSensors, PointerSensor, useSensor } from '@dnd-kit/core'
import { IPostGroup, PostsCalendarDay } from './PostsCalendarDay'
import { message } from 'services/snackbar'
import { DraggableCalendarPost } from './PostsCalendarDay/PostsCalendarDay'
import { format } from 'date-fns'
import { TZDate } from '@date-fns/tz'
import PostsDayPopup from '../PostsDayPopup'

export const INVALID_DROP_MESSAGE = `Oops!.. You can't drop your post there. Please drag it to a valid time slot outlined in green.`
export const DRAG_DROP_WARNING = 'Drag-and-drop re-ordering is only available when a single profile is selected.'
  + ' Please double-click any profile in the sidebar to select it alone. Then try again.'
const SNACK_DURATION_LONG = 5000

interface PostsCalendarViewProps {
  mode: 'month' | 'week'
  posts: PlannedPost[]
  year: number
  monthIndex: number
  loading: boolean
  activePostId?: string
  className?: string
  selection: WeekSelection
  dimPosted?: boolean
  dimPastDays?: boolean
  onPostClick: (post: PlannedPost) => void
  onPostOrderChanged?: (movedPost: PlannedPost, targetPost: PlannedPost) => void
}

export type WeekSelection = {
  date: Date
  offset: number
}

export const PostsCalendarView = memo(function PostsCalendarView(props: PostsCalendarViewProps) {
  const { posts, dimPosted, onPostOrderChanged } = props
  const dispatch = useDispatch()
  const weekViewExpanded = useSelector(postsWeekViewExpandedSelector)
  const weekStartDay = useSelector(calendarWeekStartSelector)
  const monthStart = useSelector(calendarMonthStartSelector)
  const postSize = useSelector(calendarPostSizeSelector)
  const selectedProfiles = useSelector(selectedProfilesSelector)
  const [movingPost, setMovingPost] = useState<PlannedPost | null>(null)
  const [expandedDay, setExpandedDay] = useState<CalendarDay | null>(null)
  const [dayPopupAnchor, setDayPopupAnchor] = useState<HTMLElement | null>(null)

  const { postsByDay, postsMap } = useMemo(() => {
    let order = 0
    let index = 0
    const postIndex: Record<string, PlannedPost & { index: number }> = {}
    const grouped = props.posts.reduce((groups: { [title: string]: IPostGroup }, post: PlannedPost) => {
      if (!post.isEmpty && !post.isPosted) {
        postIndex[post.id] = { ...post, index: index++ }
      }

      const postTime = format(new TZDate(post.time, post.timezone), 'M-d-yyyy')
      const group = groups[postTime]
      if (!group) {
        groups[postTime] = {
          posts: [post],
          order: order + 1,
          date: postTime
        }
        order += 1
      } else {
        group.posts.push(post)
      }
      return groups
    }, {})
    return { postsMap: postIndex, postsByDay: grouped }
  }, [props.posts])

  const closeDayPopup = () => {
    setExpandedDay(null)
    setDayPopupAnchor(null)
  }

  const onExpandDay = (day: CalendarDay, container: HTMLDivElement) => {
    setExpandedDay(day)
    setDayPopupAnchor(container)
  }

  const expandedDayPosts = useMemo(() => {
    if (!expandedDay) {
      return []
    }

    const date = expandedDay.date.getDate()
    const month = expandedDay.date.getMonth()
    return props.posts.filter(post => {
      const postTime = new Date(post.time)
      const postDate = postTime.getDate()
      const postMonth = postTime.getMonth()
      return postDate === date && postMonth === month
    })
  }, [props.posts, expandedDay])

  const toggleWeekViewExpanded = useCallback(() => {
    dispatch(setWeekViewExpanded(!weekViewExpanded))
  }, [weekViewExpanded, dispatch])

  const renderPostsForTime = useCallback((day: CalendarDay, hour?: number) => {
    const postsForTime = props.posts.filter(post => {
      const postTime = format(new TZDate(post.time, post.timezone), 'd')

      const postDate = parseInt(postTime, 10)
      return postDate === day.date.getDate() && (typeof hour === 'undefined' || post.hoursLocal === hour)
    })

    if (postsForTime.length === 0) {
      return null
    }

    return postsForTime.map(post => {
      const isActive = props.activePostId === post.id
      let dndClass: string = ''
      if (
        !post.isVirtual
        && !post.isPosted
        && post.autoPost
        && movingPost
        && movingPost.profileId === post.profileId
        && movingPost.id !== post.id
        && movingPost.bucketId === post.bucketId
      ) {
        dndClass = styles['drop-target']
      }
      return (
        <DraggableCalendarPost
          key={post.id}
          post={post}
          size={postSize}
          containerClassName={dndClass}
          className={`${styles['mini-post']} ${isActive ? styles.active : ''}`}
          onClick={props.onPostClick}
        />
      )
    })
  }, [props.activePostId, props.onPostClick, props.posts, postSize, movingPost])

  const renderPostsForDay = useCallback((day: CalendarDay) => {
    const date = day.date.getDate()
    const month = day.date.getMonth() + 1
    const year = day.date.getFullYear()
    const group = postsByDay[`${month}-${date}-${year}`] || {
      posts: [],
      order: 0,
      date: `${month}-${date}`
    }

    return (
      <PostsCalendarDay
        day={day}
        postGroup={group}
        withDragAndDrop={props.mode === 'month'}
        draggedPost={movingPost}
        postSize={postSize}
        activePostId={props.activePostId}
        dimPosted={dimPosted}
        onPostClick={props.onPostClick}
        onExpandDay={onExpandDay}
      />
    )
  }, [postsByDay, props.mode, props.activePostId, props.onPostClick, movingPost, postSize, dimPosted])

  const onDragEnd = useCallback((event: any) => {
    if (selectedProfiles.length > 1) {
      return
    }
    if (props.mode === 'week') {
      const fromPost = postsMap[movingPost?.id || '']
      const toPost = postsMap[event.over?.data?.current?.post?.id || '']
      setMovingPost(null)
      if (
        !toPost
        || !toPost.autoPost
        || toPost.profileId !== fromPost.profileId
        || fromPost?.bucketId !== toPost?.bucketId
      ) {
        dispatch(message(INVALID_DROP_MESSAGE, 'warning', SNACK_DURATION_LONG, true))
        return
      }
      if (onPostOrderChanged) {
        onPostOrderChanged(fromPost, toPost)
      }
      return
    }
    const { active, over } = event
    const draggedPost = movingPost
    setMovingPost(null)
    if (!over) {
      return
    }

    if (draggedPost && active.id !== over.id) {
      const targetPost = postsMap[over.id]

      // EXPL: Check if post is dropped at a valid slot:
      //  - not on a scheduled post (not autoPost)
      //  - not on post of different profile
      //  - slots buckets not matching
      if (
        !targetPost
        || !targetPost.autoPost
        || targetPost.profileId !== draggedPost.profileId
        || draggedPost?.bucketId !== targetPost?.bucketId
      ) {
        dispatch(message(INVALID_DROP_MESSAGE, 'warning', SNACK_DURATION_LONG, true))
        return
      }

      if (!draggedPost || !targetPost) {
        console.error('[onDragEnd()] Error: Source or target Post not found.')
        return
      }

      if (onPostOrderChanged) {
        onPostOrderChanged(draggedPost, targetPost)
      }
    }
  }, [dispatch, movingPost, onPostOrderChanged, selectedProfiles.length, props.mode, postsMap])

  const onDragStart = (start: any) => {
    if (selectedProfiles.length > 1) {
      dispatch(message(DRAG_DROP_WARNING, 'info', SNACK_DURATION_LONG))
    } else {
      setMovingPost(start.active?.data?.current?.post || null)
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8
      }
    })
  )

  return (
    <DndContext
      sensors={sensors}
      measuring={{ strategy: MeasuringStrategy.Always }}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}
    >
      <div className={props.className || ''}>
        {props.mode === 'month' ? (
          <CalendarMonthView
            month={props.monthIndex}
            year={props.year}
            startDay={weekStartDay}
            monthStart={monthStart}
            dimPastDays={props.dimPastDays}
            renderDay={renderPostsForDay}
            className={styles['calendar-month']}
          />
        ) : (
          <CalendarWeekView
            referenceDate={props.selection.date}
            expanded={weekViewExpanded}
            isEmpty={props.posts.length === 0}
            className={styles['calendar-week']}
            startDay={weekStartDay}
            renderCell={renderPostsForTime}
            highlightCurrentDay
            onToggleExpanded={toggleWeekViewExpanded}
          />
        )}
        <Backdrop open={props.loading} className={styles.backdrop}>
          <SimpleLoader />
        </Backdrop>
        <PostsDayPopup
          anchor={props.loading ? null : dayPopupAnchor}
          posts={expandedDayPosts}
          day={expandedDay}
          activePostId={props.activePostId}
          postSize={postSize}
          onPostClick={props.onPostClick}
          onClose={closeDayPopup}
        />
      </div>
    </DndContext>
  )
})
